import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SelectDropdown, type SelectDropdownOption } from "./ui/SelectDropdown";

// Import flag assets
import trFlag from "../assets/flags/tr.svg";
import enFlag from "../assets/flags/en.svg";
import itFlag from "../assets/flags/it.svg";
import deFlag from "../assets/flags/de.svg";
import esFlag from "../assets/flags/es.svg";
import frFlag from "../assets/flags/fr.svg";

interface NavbarProps {
  theme: "light" | "dark";
  toggleTheme: () => void;
}

interface NavTabsProps {
  tabs: { id: string; label: string }[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

interface LanguageSelectorProps {
  currentLang: string;
  onLanguageChange: (lang: string) => void;
  isMobile?: boolean;
}

function NavTabs({ tabs, activeTab, onTabChange }: NavTabsProps) {
  return (
    <nav className="overflow-x-auto scrollbar-none py-1 -my-1" aria-label="Main Navigation">
      <ul className="flex items-center gap-1 min-w-max relative">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <li
              key={tab.id}
              style={isActive ? { anchorName: "--active-tab" } as React.CSSProperties : undefined}
              className={`${isActive ? "active" : ""} relative`}
            >
              <button
                onClick={() => onTabChange(tab.id)}
                className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors relative z-10 ${isActive
                  ? "text-brand-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  }`}
              >
                {tab.label}
              </button>
            </li>
          );
        })}
        <li className="tab-underline hidden md:block" role="none" aria-hidden="true" />
      </ul>
    </nav>
  );
}

function LanguageSelector({ currentLang, onLanguageChange, isMobile }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const triggerClass = isMobile
    ? "w-[85px] h-9"
    : "w-[95px] hover:bg-muted/50 transition-colors";
  const comingSoonText = t("nav.comingSoon", "Coming soon");

  const options: SelectDropdownOption[] = [
    { value: "tr", label: "TR", icon: trFlag },
    { value: "en", label: "EN", icon: enFlag },
    { value: "it", label: "IT", icon: itFlag, disabled: true, tooltip: comingSoonText },
    { value: "de", label: "DE", icon: deFlag, disabled: true, tooltip: comingSoonText },
    { value: "es", label: "ES", icon: esFlag, disabled: true, tooltip: comingSoonText },
    { value: "fr", label: "FR", icon: frFlag, disabled: true, tooltip: comingSoonText },
  ];

  return (
    <SelectDropdown
      value={currentLang}
      onChange={onLanguageChange}
      options={options}
      className={triggerClass}
      ariaLabel={t("nav.selectLanguage", "Select Language")}
    />
  );
}

export function Navbar({ theme, toggleTheme }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentLang = pathParts[0] || "en";
  const activeTab = pathParts[1] || "home";

  const tabs = [
    { id: "home", label: t("nav.home") },
    { id: "professional", label: t("nav.professional") },
    { id: "academic", label: t("nav.academic") },
    { id: "community", label: t("nav.community") },
    { id: "projects", label: t("nav.projects", "Projects") },
    { id: "hobby", label: t("nav.hobby") },
    { id: "blog", label: t("nav.blog", "Blog") },
    { id: "contact", label: t("nav.contact") }
  ];

  const handleTabChange = (tabId: string) => {
    navigate(`/${currentLang}/${tabId}`);
  };

  const handleLanguageChange = (newLang: string) => {
    i18n.changeLanguage(newLang);
    navigate(`/${newLang}/${activeTab}`);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-background/85 backdrop-blur-md border-b border-border/80 px-4 py-3 md:px-8">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center justify-between">
          <h1
            onClick={() => handleTabChange("home")}
            className="text-xl font-bold tracking-tight text-brand-primary cursor-pointer hover:opacity-85 transition-opacity"
          >
            Uğurcan Uzunkaya
          </h1>

          {/* Mobile Actions */}
          <div className="flex items-center gap-3 md:hidden">
            <div className="theme-switch-container scale-90 origin-right">
              <input
                id="theme-toggle-mobile"
                type="checkbox"
                checked={theme === "light"}
                onChange={toggleTheme}
                aria-label="Toggle Theme"
              />
              <label className="toggle" htmlFor="theme-toggle-mobile">
                <i />
              </label>
            </div>
            <LanguageSelector
              currentLang={currentLang}
              onLanguageChange={handleLanguageChange}
              isMobile
            />
          </div>
        </div>

        {/* Navigation Tabs bar */}
        <NavTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3.5">
          <div className="theme-switch-container">
            <input
              id="theme-toggle-desktop"
              type="checkbox"
              checked={theme === "light"}
              onChange={toggleTheme}
              aria-label="Toggle Theme"
            />
            <label className="toggle" htmlFor="theme-toggle-desktop">
              <i />
            </label>
          </div>

          <LanguageSelector
            currentLang={currentLang}
            onLanguageChange={handleLanguageChange}
          />
        </div>
      </div>
    </header>
  );
}

export default Navbar;
